import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Switch, 
  FormControlLabel, 
  TextField, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Divider, 
  Avatar, 
  IconButton, 
  Chip, 
  Alert, 
  Tab, 
  Tabs, 
  Slider,
  RadioGroup,
  Radio
} from '@mui/material';
import {
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  Palette as PaletteIcon,
  Phone as PhoneIcon,
  VolumeUp as VolumeUpIcon,
  Headset as HeadsetIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  Support as SupportIcon
} from '@mui/icons-material';

const SupportSettings = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [settings, setSettings] = useState({
    // Profile Settings
    agentName: 'সাপোর্ট এজেন্ট',
    agentId: 'SA001',
    email: 'agent@grameenbazar.com',
    phone: '01712345678',
    department: 'কাস্টমার সাপোর্ট',
    shift: 'সকাল ৯টা - সন্ধ্যা ৬টা',
    
    // Notification Settings
    emailNotifications: true,
    smsNotifications: false,
    desktopNotifications: true,
    orderNotifications: true,
    returnNotifications: true,
    urgentTicketNotifications: true,
    newCustomerNotifications: false,
    
    // Call Settings
    autoAnswer: false,
    callRecording: true,
    callTransfer: true,
    holdMusic: true,
    callVolume: 75,
    micVolume: 80,
    
    // System Preferences
    language: 'bn',
    theme: 'light',
    autoRefresh: true,
    refreshInterval: 30,
    pageSize: 20,
    
    // Dashboard Preferences
    showQuickActions: true,
    showStatistics: true,
    showRecentOrders: true,
    showUrgentTickets: true,
    compactView: false,
    
    // Security Settings
    sessionTimeout: 60,
    twoFactorAuth: false,
    passwordExpiry: 90
  });

  const [editMode, setEditMode] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = () => {
    // Here you would save settings to backend
    console.log('Saving settings:', settings);
    setSaveSuccess(true);
    setEditMode(false);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const tabLabels = [
    { label: 'প্রোফাইল', icon: <PersonIcon /> },
    { label: 'নোটিফিকেশন', icon: <NotificationsIcon /> },
    { label: 'কল সেটিংস', icon: <PhoneIcon /> },
    { label: 'ড্যাশবোর্ড', icon: <DashboardIcon /> },
    { label: 'সিস্টেম', icon: <SettingsIcon /> },
    { label: 'নিরাপত্তা', icon: <SecurityIcon /> }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ 
            background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            সাপোর্ট সেটিংস
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            আপনার সাপোর্ট সিস্টেম কনফিগার করুন
          </Typography>
        </Box>
        <Box>
          {editMode ? (
            <>
              <Button 
                variant="outlined" 
                onClick={() => setEditMode(false)}
                sx={{ mr: 1 }}
              >
                বাতিল
              </Button>
              <Button 
                variant="contained" 
                startIcon={<SaveIcon />}
                onClick={handleSave}
                sx={{ 
                  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
                  color: 'white'
                }}
              >
                সেভ করুন
              </Button>
            </>
          ) : (
            <Button 
              variant="contained" 
              startIcon={<EditIcon />}
              onClick={() => setEditMode(true)}
              sx={{ 
                background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
                color: 'white'
              }}
            >
              এডিট করুন
            </Button>
          )}
        </Box>
      </Box>

      {/* Success Alert */}
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          সেটিংস সফলভাবে সংরক্ষিত হয়েছে!
        </Alert>
      )}

      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minWidth: 120
            }
          }}
        >
          {tabLabels.map((tab, index) => (
            <Tab 
              key={index}
              label={tab.label} 
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {/* Profile Settings */}
      {currentTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar 
                  sx={{ 
                    width: 100, 
                    height: 100, 
                    mx: 'auto', 
                    mb: 2,
                    background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
                    fontSize: '2rem'
                  }}
                >
                  SA
                </Avatar>
                <Typography variant="h6">{settings.agentName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  এজেন্ট আইডি: {settings.agentId}
                </Typography>
                <Chip 
                  label={settings.department} 
                  color="primary" 
                  sx={{ mt: 1 }}
                />
                {editMode && (
                  <Button 
                    variant="outlined" 
                    size="small" 
                    sx={{ mt: 2, display: 'block', mx: 'auto' }}
                  >
                    ছবি পরিবর্তন করুন
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>ব্যক্তিগত তথ্য</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="নাম"
                    value={settings.agentName}
                    onChange={(e) => handleSettingChange('agentName', e.target.value)}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="এজেন্ট আইডি"
                    value={settings.agentId}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ইমেইল"
                    type="email"
                    value={settings.email}
                    onChange={(e) => handleSettingChange('email', e.target.value)}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ফোন নম্বর"
                    value={settings.phone}
                    onChange={(e) => handleSettingChange('phone', e.target.value)}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!editMode}>
                    <InputLabel>বিভাগ</InputLabel>
                    <Select
                      value={settings.department}
                      label="বিভাগ"
                      onChange={(e) => handleSettingChange('department', e.target.value)}
                    >
                      <MenuItem value="কাস্টমার সাপোর্ট">কাস্টমার সাপোর্ট</MenuItem>
                      <MenuItem value="টেকনিক্যাল সাপোর্ট">টেকনিক্যাল সাপোর্ট</MenuItem>
                      <MenuItem value="অর্ডার সাপোর্ট">অর্ডার সাপোর্ট</MenuItem>
                      <MenuItem value="ডেলিভারি সাপোর্ট">ডেলিভারি সাপোর্ট</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!editMode}>
                    <InputLabel>শিফট</InputLabel>
                    <Select
                      value={settings.shift}
                      label="শিফট"
                      onChange={(e) => handleSettingChange('shift', e.target.value)}
                    >
                      <MenuItem value="সকাল ৯টা - সন্ধ্যা ৬টা">সকাল ৯টা - সন্ধ্যা ৬টা</MenuItem>
                      <MenuItem value="সন্ধ্যা ৬টা - রাত ২টা">সন্ধ্যা ৬টা - রাত ২টা</MenuItem>
                      <MenuItem value="রাত ১০টা - সকাল ৬টা">রাত ১০টা - সকাল ৬টা</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Notification Settings */}
      {currentTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>সাধারণ নোটিফিকেশন</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.emailNotifications}
                      onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="ইমেইল নোটিফিকেশন"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.smsNotifications}
                      onChange={(e) => handleSettingChange('smsNotifications', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="SMS নোটিফিকেশন"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.desktopNotifications}
                      onChange={(e) => handleSettingChange('desktopNotifications', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="ডেস্কটপ নোটিফিকেশন"
                />
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>কার্যকলাপ নোটিফিকেশন</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.orderNotifications}
                      onChange={(e) => handleSettingChange('orderNotifications', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="নতুন অর্ডার"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.returnNotifications}
                      onChange={(e) => handleSettingChange('returnNotifications', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="রিটার্ন রিকোয়েস্ট"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.urgentTicketNotifications}
                      onChange={(e) => handleSettingChange('urgentTicketNotifications', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="জরুরি টিকেট"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.newCustomerNotifications}
                      onChange={(e) => handleSettingChange('newCustomerNotifications', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="নতুন গ্রাহক"
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Call Settings */}
      {currentTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>কল কনফিগারেশন</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.autoAnswer}
                      onChange={(e) => handleSettingChange('autoAnswer', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="অটো উত্তর"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.callRecording}
                      onChange={(e) => handleSettingChange('callRecording', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="কল রেকর্ডিং"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.callTransfer}
                      onChange={(e) => handleSettingChange('callTransfer', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="কল ট্রান্সফার"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.holdMusic}
                      onChange={(e) => handleSettingChange('holdMusic', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="হোল্ড মিউজিক"
                />
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>অডিও সেটিংস</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography gutterBottom>কল ভলিউম</Typography>
                  <Slider
                    value={settings.callVolume}
                    onChange={(e, value) => handleSettingChange('callVolume', value)}
                    disabled={!editMode}
                    valueLabelDisplay="auto"
                    step={5}
                    marks
                    min={0}
                    max={100}
                  />
                </Box>
                <Box>
                  <Typography gutterBottom>মাইক ভলিউম</Typography>
                  <Slider
                    value={settings.micVolume}
                    onChange={(e, value) => handleSettingChange('micVolume', value)}
                    disabled={!editMode}
                    valueLabelDisplay="auto"
                    step={5}
                    marks
                    min={0}
                    max={100}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Dashboard Settings */}
      {currentTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>ড্যাশবোর্ড লেআউট</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.showQuickActions}
                      onChange={(e) => handleSettingChange('showQuickActions', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="দ্রুত কার্যক্রম দেখান"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.showStatistics}
                      onChange={(e) => handleSettingChange('showStatistics', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="পরিসংখ্যান দেখান"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.showRecentOrders}
                      onChange={(e) => handleSettingChange('showRecentOrders', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="সাম্প্রতিক অর্ডার দেখান"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.showUrgentTickets}
                      onChange={(e) => handleSettingChange('showUrgentTickets', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="জরুরি টিকেট দেখান"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.compactView}
                      onChange={(e) => handleSettingChange('compactView', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="কমপ্যাক্ট ভিউ"
                />
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>ডেটা প্রদর্শন</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>পেজ সাইজ</InputLabel>
                  <Select
                    value={settings.pageSize}
                    label="পেজ সাইজ"
                    onChange={(e) => handleSettingChange('pageSize', e.target.value)}
                  >
                    <MenuItem value={10}>১০</MenuItem>
                    <MenuItem value={20}>২০</MenuItem>
                    <MenuItem value={50}>৫০</MenuItem>
                    <MenuItem value={100}>১০০</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.autoRefresh}
                      onChange={(e) => handleSettingChange('autoRefresh', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="অটো রিফ্রেশ"
                />
                
                {settings.autoRefresh && (
                  <FormControl fullWidth disabled={!editMode}>
                    <InputLabel>রিফ্রেশ ইন্টারভাল (সেকেন্ড)</InputLabel>
                    <Select
                      value={settings.refreshInterval}
                      label="রিফ্রেশ ইন্টারভাল (সেকেন্ড)"
                      onChange={(e) => handleSettingChange('refreshInterval', e.target.value)}
                    >
                      <MenuItem value={15}>১৫</MenuItem>
                      <MenuItem value={30}>৩০</MenuItem>
                      <MenuItem value={60}>৬০</MenuItem>
                      <MenuItem value={120}>১২০</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* System Settings */}
      {currentTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>ভাষা ও থিম</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>ভাষা</InputLabel>
                  <Select
                    value={settings.language}
                    label="ভাষা"
                    onChange={(e) => handleSettingChange('language', e.target.value)}
                  >
                    <MenuItem value="bn">বাংলা</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </Select>
                </FormControl>
                
                <Box>
                  <Typography gutterBottom>থিম</Typography>
                  <RadioGroup
                    value={settings.theme}
                    onChange={(e) => handleSettingChange('theme', e.target.value)}
                  >
                    <FormControlLabel 
                      value="light" 
                      control={<Radio disabled={!editMode} />} 
                      label="লাইট থিম" 
                    />
                    <FormControlLabel 
                      value="dark" 
                      control={<Radio disabled={!editMode} />} 
                      label="ডার্ক থিম" 
                    />
                    <FormControlLabel 
                      value="auto" 
                      control={<Radio disabled={!editMode} />} 
                      label="অটো (সিস্টেম অনুযায়ী)" 
                    />
                  </RadioGroup>
                </Box>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>সিস্টেম তথ্য</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2">
                  <strong>সিস্টেম ভার্সন:</strong> ১.২.৩
                </Typography>
                <Typography variant="body2">
                  <strong>শেষ আপডেট:</strong> ১৫ জানুয়ারি, ২০২৪
                </Typography>
                <Typography variant="body2">
                  <strong>ব্রাউজার:</strong> Chrome 120.0.0
                </Typography>
                <Typography variant="body2">
                  <strong>সার্ভার স্ট্যাটাস:</strong> 
                  <Chip label="সক্রিয়" color="success" size="small" sx={{ ml: 1 }} />
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Button variant="outlined" disabled={!editMode}>
                  ক্যাশ ক্লিয়ার করুন
                </Button>
                <Button variant="outlined" disabled={!editMode}>
                  সেটিংস রিসেট করুন
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Security Settings */}
      {currentTab === 5 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>নিরাপত্তা সেটিংস</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.twoFactorAuth}
                      onChange={(e) => handleSettingChange('twoFactorAuth', e.target.checked)}
                      disabled={!editMode}
                    />
                  }
                  label="দ্বি-ফ্যাক্টর প্রমাণীকরণ"
                />
                
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>সেশন টাইমআউট (মিনিট)</InputLabel>
                  <Select
                    value={settings.sessionTimeout}
                    label="সেশন টাইমআউট (মিনিট)"
                    onChange={(e) => handleSettingChange('sessionTimeout', e.target.value)}
                  >
                    <MenuItem value={30}>৩০</MenuItem>
                    <MenuItem value={60}>৬০</MenuItem>
                    <MenuItem value={120}>১২০</MenuItem>
                    <MenuItem value={240}>২৪০</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>পাসওয়ার্ড মেয়াদ (দিন)</InputLabel>
                  <Select
                    value={settings.passwordExpiry}
                    label="পাসওয়ার্ড মেয়াদ (দিন)"
                    onChange={(e) => handleSettingChange('passwordExpiry', e.target.value)}
                  >
                    <MenuItem value={30}>৩০</MenuItem>
                    <MenuItem value={60}>৬০</MenuItem>
                    <MenuItem value={90}>৯০</MenuItem>
                    <MenuItem value={180}>১৮০</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>পাসওয়ার্ড পরিবর্তন</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="বর্তমান পাসওয়ার্ড"
                  type="password"
                  disabled={!editMode}
                />
                <TextField
                  fullWidth
                  label="নতুন পাসওয়ার্ড"
                  type="password"
                  disabled={!editMode}
                />
                <TextField
                  fullWidth
                  label="নতুন পাসওয়ার্ড নিশ্চিত করুন"
                  type="password"
                  disabled={!editMode}
                />
                <Button 
                  variant="contained" 
                  disabled={!editMode}
                  sx={{ mt: 2 }}
                >
                  পাসওয়ার্ড আপডেট করুন
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default SupportSettings; 